import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

// One reorderable / resizable column header, shared by the Screener, Ward and
// Referrals tables. Dragging the header reorders; the handle on the right edge
// resizes. When `onSort` is given the label is a sort button; otherwise it is a
// plain (non-sortable) label. Generic over the panel's column-id union.

const COLUMN_DRAG_MIME = 'application/x-corryk-col';

export function ColumnHeaderCell<Id extends string>({
  col,
  columns,
  label,
  numeric,
  active,
  sortDir,
  onSort,
  moveColumn,
  setColumnWidth,
  tooltip,
}: {
  col: { id: Id; width: number };
  columns: ReadonlyArray<{ id: Id }>;
  label: string;
  numeric: boolean;
  active?: boolean;
  sortDir?: 'asc' | 'desc';
  onSort?: (id: Id) => void;
  moveColumn: (id: Id, targetIndex: number) => void;
  setColumnWidth: (id: Id, px: number) => void;
  tooltip?: string;
}) {
  const [edge, setEdge] = useState<'left' | 'right' | null>(null);

  function onResizeDown(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = col.width;
    const onMove = (ev: MouseEvent) => setColumnWidth(col.id, startW + (ev.clientX - startX));
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }

  const labelStyle: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: numeric ? 'flex-end' : 'flex-start',
    gap: 4,
    padding: '0 var(--s-2)',
    background: 'transparent',
    border: 'none',
    cursor: onSort ? 'pointer' : 'default',
    fontSize: 'var(--t-meta)',
    fontWeight: active ? 700 : 500,
    color: active ? 'var(--accent)' : 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  };

  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData(COLUMN_DRAG_MIME, col.id);
      }}
      onDragOver={(e) => {
        if (!e.dataTransfer.types.includes(COLUMN_DRAG_MIME)) return;
        e.preventDefault();
        const r = e.currentTarget.getBoundingClientRect();
        setEdge(e.clientX < r.left + r.width / 2 ? 'left' : 'right');
      }}
      onDragLeave={() => setEdge(null)}
      onDragEnd={() => setEdge(null)}
      onDrop={(e) => {
        e.preventDefault();
        const draggedId = e.dataTransfer.getData(COLUMN_DRAG_MIME) as Id;
        const r = e.currentTarget.getBoundingClientRect();
        const dropRight = e.clientX >= r.left + r.width / 2;
        setEdge(null);
        if (!draggedId || draggedId === col.id) return;
        const fromIndex = columns.findIndex((c) => c.id === draggedId);
        const targetIndex = columns.findIndex((c) => c.id === col.id);
        if (fromIndex === -1 || targetIndex === -1) return;
        const newIndex = dropRight ? targetIndex + 1 : targetIndex;
        const adjusted = fromIndex < newIndex ? newIndex - 1 : newIndex;
        moveColumn(draggedId, adjusted);
      }}
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'stretch',
        minWidth: 0,
        // Drop indicator drawn with an inset box-shadow rather than a border so
        // it never consumes layout width — a transparent 2px border would shift
        // the header label out of alignment with the (border-less) body cells.
        boxShadow:
          edge === 'left'
            ? 'inset 2px 0 0 var(--accent)'
            : edge === 'right'
              ? 'inset -2px 0 0 var(--accent)'
              : undefined,
      }}
    >
      {onSort ? (
        <button type="button" onClick={() => onSort(col.id)} title={tooltip} style={labelStyle}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
          {active && (sortDir === 'asc' ? <ChevronUp size={10} /> : <ChevronDown size={10} />)}
        </button>
      ) : (
        <div title={tooltip} style={labelStyle}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
        </div>
      )}
      <div
        onMouseDown={onResizeDown}
        onClick={(e) => e.stopPropagation()}
        title="Drag to resize"
        aria-hidden="true"
        style={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: 7,
          cursor: 'col-resize',
          userSelect: 'none',
        }}
      />
    </div>
  );
}
